import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

import Loader3D from '../../../components/Loader/Loader3D';
import apiRequest from '../../../api/request';
import DeveloperNote from './DeveloperNote';

/**
 * WHAT IS BEHIND ONE ADMISSIONS NUMBER.
 *
 * Clicking the Admissions cell on the OGD Point Tracker opens this: every student
 * that person brought in during the chosen period.
 *
 * ONE ROW PER STUDENT, NOT PER COURSE
 *
 * The column counts students, so the popup lists students, and the two always add
 * up to the same number. A student who took two courses is one row with both
 * courses named on it.
 *
 * EVERY ROW SAYS WHY IT LANDED IN THIS MONTH
 *
 * The date its first real payment arrived. No payment, no admission, so there is
 * no other case to explain. That is the most asked question about this column, so
 * it is answered on the row.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "2026-04-07" shown as "07 Apr 2026". */
const prettyDate = (raw) => {
  if (!raw) return '';
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(raw);

  const [, year, month, day] = match;
  return `${day} ${MONTHS[Number(month) - 1]} ${year}`;
};

export default function AdmissionDetailDialog({
  open,
  onClose,
  person, // { userId, oga, team, admissions }
  fromDate,
  toDate,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !person?.userId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const res = await apiRequest({
        method: 'get',
        url:
          `ogd-point-tracker/admissions?userId=${person.userId}` +
          `&fromDate=${fromDate}&toDate=${toDate}`,
      });
      if (cancelled) return;

      if (res?.status === 'success') {
        setRows(res.data?.rows || []);
        setMeta(res.data?.meta || null);
      } else {
        setError(res?.message || 'Could not load the admissions');
      }
      setLoading(false);
    })();

    // a fast second click must not let the first answer overwrite the second
    return () => {
      cancelled = true;
    };
  }, [open, person?.userId, fromDate, toDate]);

  // the cell said one number and the list has another. Said out loud rather than
  // left for somebody to notice by counting
  const mismatch =
    !loading &&
    !error &&
    person?.admissions != null &&
    Number(person.admissions) !== rows.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      // the box is nearly the whole window. These tables are wide and are
      // read side by side against the number on the cell, so scrolling a
      // narrow box was the slowest part of checking a figure.
      PaperProps={{ sx: { height: '92vh', maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Admissions · {person?.oga}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {rows.length} {rows.length === 1 ? 'student' : 'students'}
              {meta?.courses != null &&
                meta.courses !== rows.length &&
                ` · ${meta.courses} courses`}
              {person?.team ? ` · ${person.team}` : ''}
              {fromDate ? ` · ${fromDate} to ${toDate}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Icon icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <Loader3D />}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="info" icon={<Icon icon="mdi:account-off-outline" />}>
            No admissions in this period.
          </Alert>
        )}

        {mismatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The table shows {String(person.admissions)} for this period but{' '}
            {rows.length} are listed here. The two should always match.
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && (
          <TableContainer sx={{ maxHeight: 'calc(92vh - 190px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell sx={{ width: 130 }}>Admission no</TableCell>
                  <TableCell
                    sx={{ width: 90 }}
                    title="How many courses this student took. The column still counts the student once."
                  >
                    Courses
                  </TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell
                    sx={{ width: 170 }}
                    title="The date their first real payment arrived, which is what put them in this month"
                  >
                    First payment
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, index) => (
                  <TableRow key={r.admnNo} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{r.name || ''}</TableCell>
                    <TableCell>{r.admnNo}</TableCell>
                    <TableCell>
                      {r.courses.length > 1 ? (
                        // two courses, still one admission. Marked so the row does
                        // not read as a miscount
                        <Typography
                          variant="body2"
                          sx={{ color: '#9C27B0', fontWeight: 600 }}
                        >
                          {r.courses.length}
                        </Typography>
                      ) : (
                        <Typography variant="body2">{r.courses.length}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.courses.join(', ')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{prettyDate(r.countedOn)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <DeveloperNote
          title="Admissions counts STUDENTS whose first real payment landed in the period"
          steps={[
            {
              what: 'Start from the OGA',
              where: 'user.userID  =  ' + (person?.userId ?? ''),
              why: 'the person whose row was clicked on the table',
            },
            {
              what: 'Find the enrolments they own, ignoring voided ones',
              where: 'course_enrollment  WHERE course_enrollment_userID = that id  AND course_enrollment_status <> -1',
              why: 'an inactive student still counts, they joined that month. Only status -1 is excluded',
            },
            {
              what: 'Work out the FIRST REAL PAYMENT of each enrolment',
              where: 'income  →  drop delete_status <> 0, income_accountant_verification = 2, income_is_hold_amount = 1  →  MIN(income_transaction_date) per income_course_enrollment_id',
              why: 'a hold amount is not a real payment. Nothing is read out of remark text',
            },
            {
              what: 'Keep the enrolment only if that first payment falls in the month',
              where: "first payment  BETWEEN '" + fromDate + "' AND '" + toDate + "'",
              why: 'this is the date that decides the month, NOT course_enrollment_timestamp',
            },
            {
              what: 'Count the students, not the courses',
              where: 'COUNT(DISTINCT course_enrollment.admn_no)   =   Admissions',
              why: 'a student who bought two courses is one admission and one row in this list',
            },
          ]}
          lines={[
            'No real payment means no admission at all. There is no fallback to sro_admission_confirmed_on.',
            'income_is_hold_amount is barely filled in on older data, so for older months a hold can count as the first real payment and put an admission in the wrong month.',
          ]}
        />
      </DialogContent>
    </Dialog>
  );
}
